import React, { useMemo } from "react";
import { OtcRequestItem } from "../../../otc-requests/types";
import { normalizeTransactionStatus } from "../../utils/statusLookup";
import { OtcCustomerDetailsCol } from "./OtcCustomerDetailsCol";
import { OtcRatesDetailsCol } from "./OtcRatesDetailsCol";

export interface OtcOverviewCardProps {
  request: OtcRequestItem;
  onRefreshStatus?: () => void;
  onViewWallet?: () => void;
}

const formatTimestampWithTime = (rawTs?: string): string => {
  if (!rawTs || rawTs === "-") return "-";
  // If it already includes a formatted time (e.g., contains ' • ' or ':')
  if (
    rawTs.includes(" • ") ||
    (rawTs.includes(":") && (rawTs.includes("AM") || rawTs.includes("PM")))
  ) {
    return rawTs;
  }
  const parsed = new Date(rawTs);
  if (!isNaN(parsed.getTime())) {
    const formattedDate = parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const formattedTime = parsed.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${formattedDate} • ${formattedTime}`;
  }
  return rawTs;
};

export const OtcOverviewCard: React.FC<OtcOverviewCardProps> = ({
  request,
  onViewWallet,
}) => {
  const stateNorm = normalizeTransactionStatus(request.state);
  const isUnderReview =
    stateNorm === "under_review" ||
    stateNorm === "new" ||
    stateNorm === "submitted" ||
    stateNorm === "pending_review";

  // Dynamic Status Stepper matching Figma lifecycles
  const resolvedSteps = useMemo(() => {
    const fallbackTimestamp = formatTimestampWithTime(request.date) || "-";

    // 1. Under review (Initial submitted state)
    if (isUnderReview) {
      return [
        {
          id: "step-1",
          title: "Request submitted",
          timestamp: request.timeline[0]?.timestamp || fallbackTimestamp,
          color: "purple",
        },
      ];
    }

    // 2. Offer sent
    if (stateNorm === "offer_sent" || stateNorm === "pending") {
      return [
        {
          id: "step-1",
          title: "Offer sent",
          timestamp: request.timeline[0]?.timestamp || fallbackTimestamp,
          color: "purple",
        },
        {
          id: "step-2",
          title: "Request submitted",
          timestamp: request.timeline[1]?.timestamp || fallbackTimestamp,
          color: "gray",
        },
      ];
    }

    // 3. Request approved
    if (stateNorm === "request_approved" || stateNorm === "approved") {
      return [
        {
          id: "step-1",
          title: "Request approved",
          timestamp: request.timeline[0]?.timestamp || fallbackTimestamp,
          color: "purple",
        },
        {
          id: "step-2",
          title: "Request submitted",
          timestamp: request.timeline[1]?.timestamp || fallbackTimestamp,
          color: "gray",
        },
      ];
    }

    // 3b. Offer accepted / Agreed / Awaiting funding / Processing
    if (
      stateNorm === "offer_accepted" ||
      stateNorm === "agreed" ||
      stateNorm === "awaiting_funding" ||
      stateNorm === "processing"
    ) {
      const step1Title =
        stateNorm === "agreed"
          ? "Agreement reached"
          : stateNorm === "awaiting_funding"
            ? "Awaiting funding"
            : stateNorm === "offer_accepted"
              ? "Offer accepted"
              : "Processing transfer";

      return [
        {
          id: "step-1",
          title: step1Title,
          timestamp: request.timeline[0]?.timestamp || fallbackTimestamp,
          color: "purple",
        },
        {
          id: "step-2",
          title: "Offer sent",
          timestamp: request.timeline[1]?.timestamp || fallbackTimestamp,
          color: "gray",
        },
        {
          id: "step-3",
          title: "Request submitted",
          timestamp: request.timeline[2]?.timestamp || fallbackTimestamp,
          color: "gray",
        },
      ];
    }

    // 3c. Needs approval (from Figma Complete transfer flow)
    if (
      stateNorm === "needs_approval" ||
      stateNorm === "submitted_for_approval"
    ) {
      return [
        {
          id: "step-1",
          title: "Money received",
          timestamp: request.timeline[0]?.timestamp || fallbackTimestamp,
          color: "green",
        },
        {
          id: "step-2",
          title: "Offer accepted",
          timestamp: request.timeline[1]?.timestamp || fallbackTimestamp,
          color: "purple",
        },
        {
          id: "step-3",
          title: "Offer sent",
          timestamp: request.timeline[2]?.timestamp || fallbackTimestamp,
          color: "gray",
        },
        {
          id: "step-4",
          title: "Request submitted",
          timestamp: request.timeline[3]?.timestamp || fallbackTimestamp,
          color: "gray",
        },
      ];
    }

    // 4. Completed
    if (
      stateNorm === "completed" ||
      stateNorm === "completed_transfer" ||
      stateNorm === "success"
    ) {
      return [
        {
          id: "step-1",
          title: "Transaction completed",
          timestamp: request.timeline[0]?.timestamp || fallbackTimestamp,
          color: "green",
        },
        {
          id: "step-2",
          title: "Transaction processing",
          timestamp: request.timeline[1]?.timestamp || fallbackTimestamp,
          color: "purple",
        },
        {
          id: "step-3",
          title: "Transaction initiated",
          timestamp: request.timeline[2]?.timestamp || fallbackTimestamp,
          color: "gray",
        },
      ];
    }

    // 5. Rejected / Failed / Declined / Expired / Canceled
    return [
      {
        id: "step-1",
        title: stateNorm.includes("decline")
          ? "Offer declined"
          : stateNorm.includes("expire")
            ? "Offer expired"
            : stateNorm.includes("cancel")
              ? "Request canceled"
              : stateNorm === "failed"
                ? "Transaction failed"
                : "Request rejected",
        timestamp: request.timeline[0]?.timestamp || fallbackTimestamp,
        color: "red",
      },
      {
        id: "step-2",
        title: "Request submitted",
        timestamp: request.timeline[1]?.timestamp || fallbackTimestamp,
        color: "gray",
      },
    ];
  }, [isUnderReview, stateNorm, request.timeline, request.date]);

  return (
    <div className="rounded-2xl border border-[#EEF1FB] bg-white p-6 shadow-sm">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr_0.9fr] lg:gap-0">
        {/* Column 1: Customer & Settlement Details */}
        <OtcCustomerDetailsCol
          request={request}
          onViewWallet={onViewWallet}
        />

        {/* Column 2: Request details (when Under review) vs Offer details (when Approved/Sent) */}
        <OtcRatesDetailsCol
          request={request}
          isUnderReview={isUnderReview}
        />

        {/* Column 3: Status Stepper */}
        <div className="flex flex-col border-t border-[#F2F4F7] pt-6 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
          <div className="flex h-[40px] items-center">
            <h2 className="text-[14px] font-semibold text-[#101828]">Status</h2>
          </div>

          <div className="mt-3.5 flex flex-col pt-1">
            {resolvedSteps.map((step, idx) => {
              const isLast = idx === resolvedSteps.length - 1;
              return (
                <div
                  key={step.id || `step-${idx}`}
                  className={`relative flex items-start gap-3 ${isLast ? "" : "pb-7"}`}
                >
                  {!isLast && (
                    <div className="absolute left-[6px] top-[18px] bottom-0.5 w-[2px] bg-[#EAECF0]" />
                  )}
                  <div className="z-10 mt-0.5 shrink-0">
                    <div
                      className={`h-3.5 w-3.5 rounded-full border-2 border-white ${
                        step.color === "green"
                          ? "bg-[#12B76A]"
                          : step.color === "red"
                            ? "bg-[#D92D20]"
                            : step.color === "purple"
                              ? "bg-[#6F00FF]"
                              : "bg-[#98A2B3]"
                      }`}
                    />
                  </div>
                  <div className="flex flex-col">
                    <span
                      className={`text-[13px] ${
                        idx === 0
                          ? "font-semibold text-[#101828]"
                          : "font-medium text-[#344054]"
                      }`}
                    >
                      {step.title}
                    </span>
                    <span className="text-[11px] text-[#667085]">
                      {formatTimestampWithTime(step.timestamp)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
